import React from 'react';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApButtonSecondary,
	colors,
} from '@alixpartners/ui-components';
import { grayscale } from 'polished';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { RegionalMap } from './RegionalMap';
import { Wrapper, LoadingOverlay, ButtonWrapper } from './RegionalMap.styles';
import { MAP_COLORS } from './utils';
import { STATES_MAP_CODES } from '../utils';
import { CHART_COLORS } from '../../utils';
import { getInvestigateRegionalMapData } from 'utils/apiRequests';
import isEqual from 'lodash/isEqual';
import { SettingsContext } from 'contexts/SettingsContext';

export class RegionalMapContainer extends React.Component {
	state = {
		data: [],
		isLoading: false,
	};

	componentDidMount() {
		this.fetchData();
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	componentDidUpdate(prevProps) {
		const { dimension, queryParams, selectedItem } = this.props;

		if (
			dimension.value !== prevProps.dimension.value ||
			selectedItem !== prevProps.selectedItem ||
			!isEqual(queryParams, prevProps.queryParams)
		) {
			this.fetchData();
		}
	}

	getApiParams = () => {
		const {
			dimension,
			queryParams,
			selectedCountry,
			selectedItem,
			spendBy,
		} = this.props;
		const isPurchasing = dimension.value === 'purchasing';
		const showState = !!STATES_MAP_CODES[selectedCountry];

		return {
			...queryParams,
			filters: [...queryParams.filters, ...(selectedItem ? [spendBy] : [])],
			filterValues: [
				...queryParams.filterValues,
				...(selectedItem ? [selectedItem] : []),
			],
			isPurchasing,
			includeState: showState,
		};
	};

	fetchData = () => {
		this.setState({ isLoading: true });

		return getInvestigateRegionalMapData(this.getApiParams())
			.then(data => {
				if (this.unmounted) return;

				this.setState({
					data,
					isLoading: false,
				});
			})
			.catch(() => {
				if (this.unmounted) return;

				this.setState({ isLoading: false });
			});
	};

	getMapSeriesData = () => {
		const { selectedCountry, selectedState } = this.props;
		const { data } = this.state;
		const countryStatesCode = this.getCountryStatesCode();
		let currentData;

		if (countryStatesCode) {
			const countryWithStates = data.find(
				item => item.code === countryStatesCode
			);
			currentData = countryWithStates && countryWithStates.states;
		} else {
			currentData = data;
		}

		const currentSelection = countryStatesCode
			? selectedState
			: selectedCountry;

		if (!currentData) {
			return [];
		}

		const config = {
			borderWidth: 0.5,
			borderColor: colors.grey8,
		};

		const mapData = currentData.map(item => ({
			...item,
			...config,
			id: item.name,
			...(currentSelection
				? {
					color:
						item.name === currentSelection ? MAP_COLORS[0] : MAP_COLORS[1],
				}
				: {}),
		}));

		const splitData = mapData.map((item) => ({
			...item,
			data: item.spendSplit.map((splitItem, index) => {
				const isInactive = currentSelection && item.name !== currentSelection;
				return splitItem.categories
					? {
						categories: splitItem.categories,
						name: splitItem.name,
						y: splitItem ? splitItem.spend : null,
						percentage: splitItem.percentage,
						color: isInactive ? grayscale(CHART_COLORS[index]) : CHART_COLORS[index],
						isTail:item.isTail
					}
					: {
						name: splitItem.name,
						y: splitItem ? splitItem.spend : null,
						percentage: splitItem.percentage,
						color: isInactive ? grayscale(CHART_COLORS[index]) : CHART_COLORS[index],
						isTail:item.isTail
					};
			}),
		}));

		return splitData;
	};

	getCountryStatesCode = () => {
		const { selectedCountry } = this.props;

		return STATES_MAP_CODES[selectedCountry] || null;
	};

	render() {
		const { isLoading } = this.state;
		const {
			onCountrySelect,
			onStateSelect,
			chartRef,
			currencySymbol,
			selectedCountry,
			hideDecimals,
		} = this.props;
		const countryWithStatesCode = this.getCountryStatesCode();

		return (
			<Wrapper>
				{isLoading && (
					<LoadingOverlay>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoadingOverlay>
				)}
				{countryWithStatesCode && (
					<ButtonWrapper>
						<ApButtonSecondary
							onClick={() => {
								onCountrySelect(null);
								onStateSelect(null);
							}}
							iconName="baseline_arrow_back"
						>
							Back
						</ApButtonSecondary>
					</ButtonWrapper>
				)}
				<RegionalMap
					key={countryWithStatesCode ? 'states-map' : 'world-map'}
					data={this.getMapSeriesData()}
					onSelect={countryWithStatesCode ? onStateSelect : onCountrySelect}
					chartRef={chartRef}
					selectedCountry={selectedCountry}
					currencySymbol={currencySymbol}
					hideDecimals={hideDecimals}
				/>
			</Wrapper>
		);
	}
}

export default props => (
	<QueryStringContext.Consumer>
		{({ spendBy, selectedItem }) => (
			<SettingsContext.Consumer>
				{({ hideDecimals }) => (
					<RegionalMapContainer
						{...props}
						spendBy={spendBy}
						selectedItem={selectedItem}
						hideDecimals={hideDecimals}
					/>
				)}
			</SettingsContext.Consumer>
		)}
	</QueryStringContext.Consumer>
);
